import { STAGES, type Answers, type StageId } from '../../api/src/programs';
import type { FieldValue, IRecord } from '../lists/ListStore';

const str = (v: FieldValue): string => (v === undefined || v === null ? '' : String(v));
const opt = (v: FieldValue): string | undefined => (str(v) || undefined);

export interface IClientView {
  rec: IRecord;
  key: string;
  name: string;
  otherNames?: string;
  birthDate?: string;
  email?: string;
  phone?: string;
  language?: string;
  uci?: string;
  number?: string;
  conflictResult?: string;
  conflictBy?: string;
  conflictOn?: string;
  idMethod?: string;
  idBy?: string;
  idOn?: string;
  notes?: string;
  /** Kept in the restricted clients list (refugee and private-sponsorship clients). */
  restricted?: boolean;
}

export function clientView(r: IRecord): IClientView {
  const f = r.fields;
  return {
    rec: r, key: r.key, name: str(f.Title), otherNames: opt(f.OtherNames), birthDate: opt(f.BirthDate), email: opt(f.Email),
    phone: opt(f.Phone), language: opt(f.Language), uci: opt(f.UCI), number: opt(f.ClientNumber),
    conflictResult: opt(f.ConflictResult), conflictBy: opt(f.ConflictBy), conflictOn: opt(f.ConflictOn),
    idMethod: opt(f.IdMethod), idBy: opt(f.IdBy), idOn: opt(f.IdOn), notes: opt(f.Notes)
  };
}

export function stageLabel(id: StageId): string {
  return STAGES.filter(s => s.id === id)[0]?.label ?? id;
}

export function stageOf(label: FieldValue): StageId {
  return STAGES.filter(s => s.label === str(label) || s.id === str(label))[0]?.id ?? 'opening';
}

export interface IApplicationView {
  rec: IRecord;
  key: string;
  title: string;
  clientKey: string;
  programId: string;
  programVersion: string;
  answers: Answers;
  stage: StageId;
  open: boolean;
  lawyer?: string;
  clerk?: string;
  opened?: string;
  retainerSigned?: string;
  representativeSigned?: string;
  restricted: boolean;
  outstanding: number;
  nextDue?: string;
  submittedOn?: string;
  submittedVia?: string;
  confirmation?: string;
  outcome?: string;
  decisionDate?: string;
  closedOn?: string;
  /** Temporary residence: when the current permit or status expires (maintained status). */
  permitExpires?: string;
  folderId?: string;
  folderUrl?: string;
}

export function applicationView(r: IRecord): IApplicationView {
  const f = r.fields;
  let answers: Answers = {};
  try {
    answers = JSON.parse(str(f.Answers) || '{}') as Answers;
  } catch {
    answers = {};
  }
  return {
    rec: r, key: r.key, title: str(f.Title), clientKey: str(f.ClientKey), programId: str(f.Program), programVersion: str(f.ProgramVersion),
    answers, stage: stageOf(f.Stage), open: f.Open !== false, lawyer: opt(f.Lawyer), clerk: opt(f.Clerk), opened: opt(f.Opened),
    retainerSigned: opt(f.RetainerSigned), representativeSigned: opt(f.RepresentativeSigned), restricted: f.Restricted === true,
    outstanding: Number(f.Outstanding) || 0, nextDue: opt(f.NextDue), submittedOn: opt(f.SubmittedOn), submittedVia: opt(f.SubmittedVia),
    confirmation: opt(f.Confirmation), outcome: opt(f.Outcome), decisionDate: opt(f.DecisionDate), closedOn: opt(f.ClosedOn), permitExpires: opt(f.PermitExpires),
    folderId: opt(f.FolderId), folderUrl: opt(f.FolderUrl)
  };
}

export interface IPersonView {
  rec: IRecord;
  key: string;
  name: string;
  role: string;
  birthDate?: string;
  relationship?: string;
}

export function personView(r: IRecord): IPersonView {
  const f = r.fields;
  return { rec: r, key: r.key, name: str(f.Title), role: str(f.Role), birthDate: opt(f.BirthDate), relationship: opt(f.Relationship) };
}

export interface IItemView {
  rec: IRecord;
  key: string;
  label: string;
  itemId: string;
  personKey: string;
  section: string;
  form?: string;
  status: string;
  changedBy?: string;
  changedOn?: string;
  documents: IAttached[];
  note?: string;
}

/** A document attached to a checklist item: its id in the library, name and link. */
export interface IAttached {
  id: string;
  name: string;
  url: string;
}

export function itemView(r: IRecord): IItemView {
  const f = r.fields;
  let documents: IAttached[] = [];
  try {
    documents = JSON.parse(str(f.Documents) || '[]') as IAttached[];
  } catch {
    documents = [];
  }
  return {
    rec: r, key: r.key, label: str(f.Title), itemId: str(f.ItemId), personKey: str(f.PersonKey), section: str(f.Section),
    form: opt(f.Form), status: str(f.Status) || 'Needed', changedBy: opt(f.ChangedBy), changedOn: opt(f.ChangedOn),
    documents, note: opt(f.Note)
  };
}

/** Statuses that still need something done. */
export const OUTSTANDING: string[] = ['Needed', 'Requested', 'Uploaded by client (check)'];

export function outstandingCount(items: { status: string }[]): number {
  return items.filter(i => OUTSTANDING.indexOf(i.status) !== -1).length;
}

/** Age in whole years on a day (YYYY-MM-DD), or undefined. */
export function ageOn(birthDate: string | undefined, today: string): number | undefined {
  const b: RegExpMatchArray | null = (birthDate ?? '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const t: RegExpMatchArray | null = today.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!b || !t) {
    return undefined;
  }
  let age: number = Number(t[1]) - Number(b[1]);
  if (Number(t[2]) < Number(b[2]) || (t[2] === b[2] && Number(t[3]) < Number(b[3]))) {
    age--;
  }
  return age;
}

/** Today in the person's own time zone, as YYYY-MM-DD. */
export function localToday(now: Date = new Date()): string {
  const y: number = now.getFullYear();
  const m: string = String(now.getMonth() + 1).padStart(2, '0');
  const d: string = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * What a client's upload page shows for a checklist item: its name, with the person's first name only
 * ("Passport (Bao)", not "Passport (Tran, Bao)"), so a forwarded link shows no client details.
 */
export function publicLabel(label: string): string {
  return label.replace(/ \(([^(),]+), ([^()]+)\)$/, ' ($2)');
}

/** What the client's upload page is given: nothing more than it shows. */
export interface IClientUploadView {
  /** The firm's name; empty for restricted files. */
  firm: string;
  restricted: boolean;
  items: { key: string; label: string }[];
}

/**
 * The client's upload page for a file. Restricted files (refugee claims, private sponsorship) show no
 * firm name and no document names, only numbered slots: someone else may see the claimant's screen.
 * Other files show the firm's name and the documents asked for, with first names only.
 */
export function clientUploadView(restricted: boolean, firm: string, items: { key: string; label: string }[]): IClientUploadView {
  return restricted
    ? { firm: '', restricted: true, items: uploadSlots(items).map(s => ({ key: s.key, label: s.slot })) }
    : { firm, restricted: false, items: items.map(i => ({ key: i.key, label: publicLabel(i.label) })) };
}

/**
 * Restricted files' numbered slots, in a fixed order (by item key) so staff see the same numbers as
 * the client: "Document 1" is the same item on both sides.
 */
export function uploadSlots<T extends { key: string; label: string }>(items: T[]): (T & { slot: string })[] {
  return items.slice().sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0)).map((i, n) => ({ ...i, slot: `Document ${n + 1}` }));
}
